import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  Row,
  NameContainer,
  UserContainer,
  Username,
  FollowButton,
  ProfilePhoto,
  Users,
  LoaderWrapper,
  EmptyState,
} from "./UserFollowersModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

import { ReactComponent as FollowIcon } from "../../assets/icons/follow.svg";
import { ReactComponent as UnfollowIcon } from "../../assets/icons/unfollow.svg";
import DefaultProfilePhoto from "../DefaultProfilePhoto/DefaultProfilePhoto";

import {
  getFollowers,
  getFollowing,
  toggleFollow,
} from "../../store/slices/user/asyncThunk";
import {
  selectFollowers,
  selectFollowing,
  selectToggleFollowPending,
  selectUser,
} from "../../store/slices/user/slice";

const UserFollowersModal = ({ isOpen, onClose, followers, userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const followersState = useSelector(selectFollowers);
  const followingState = useSelector(selectFollowing);
  const togglePendingMap = useSelector(selectToggleFollowPending);
  const currentUser = useSelector(selectUser);

  const state = followers ? followersState : followingState;
  const list = state.data ?? [];
  const isLoading = state.loading;
  const currentUserId = currentUser?.id ?? currentUser?._id;
  const currentUsername = currentUser?.username;

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (!isOpen || !userId) return;
    if (followers) {
      dispatch(getFollowers({ userId }));
    } else {
      dispatch(getFollowing({ userId }));
    }
  }, [isOpen, followers, userId, dispatch]);

  const handleFollowClick = (user) => {
    const id = user.id ?? user._id;
    dispatch(toggleFollow(id));
  };

  const handleOpenProfile = (user) => {
    if (!user?.username) return;

    onClose();
    navigate(`/profile/${user.username}`);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        customOverlayStyles={{ zIndex: 3100 }}
      >
        <ModalContainer>
          <ModalContent>
            <Row>
              <ModalTitle>{followers ? "Followers" : "Following"}</ModalTitle>
              <CloseButton onClick={onClose}>
                <CloseSvg width={20} height={20} />
              </CloseButton>
            </Row>

            <div style={{ position: "relative", width: "100%" }}>
              <Users $padding={list.length >= 10}>
                {isLoading ? (
                  <LoaderWrapper>
                    <l-line-spinner
                      size="20"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </LoaderWrapper>
                ) : list.length === 0 ? (
                  <EmptyState>
                    <span>
                      {followers ? "No followers yet" : "Not following anyone"}
                    </span>
                    <span>
                      {followers
                        ? "When someone follows this user, they'll show up here."
                        : "When this user follows someone, they'll show up here."}
                    </span>
                  </EmptyState>
                ) : (
                  list.map((user) => {
                    const id = user.id ?? user._id;
                    const isCurrentUser =
                      id === currentUserId ||
                      (!!currentUsername && user.username === currentUsername);
                    const isTogglePending = !!togglePendingMap[id];
                    return (
                      <UserContainer key={id}>
                        {user?.profilePhoto ? (
                          <ProfilePhoto
                            src={user.profilePhoto}
                            alt={`${user.username} profile photo`}
                            onClick={() => handleOpenProfile(user)}
                            style={{ cursor: "pointer" }}
                          />
                        ) : (
                          <DefaultProfilePhoto
                            size={26}
                            onClick={() => handleOpenProfile(user)}
                            style={{
                              borderRadius: "50%",
                              border: "0.684px solid #164e95",
                              flexShrink: 0,
                              cursor: "pointer",
                            }}
                          />
                        )}
                        <NameContainer
                          onClick={() => handleOpenProfile(user)}
                          style={{ cursor: "pointer" }}
                        >
                          <Username>{user.username}</Username>
                        </NameContainer>
                        {!isCurrentUser && (
                          <FollowButton
                            $isFollowed={user.isFollowing}
                            disabled={isTogglePending}
                            onClick={() => handleFollowClick(user)}
                          >
                            <>
                              {user.isFollowing ? (
                                <UnfollowIcon />
                              ) : (
                                <FollowIcon />
                              )}
                              {user.isFollowing ? "unfollow" : "follow"}
                            </>
                          </FollowButton>
                        )}
                      </UserContainer>
                    );
                  })
                )}
              </Users>
            </div>
          </ModalContent>
        </ModalContainer>
      </Modal>
    </>
  );
};

export default UserFollowersModal;
